import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { cssVar } from '@going-once/tokens';
import { Icon } from './icons';
import { focusRing, Text } from './primitives';

/**
 * A modal dialog (the design system's `Dialog`): over a scrim, focus held inside it until
 * it closes, and given back to whatever opened it.
 *
 * **How it closes is the caller's statement, not a default.** Pass `close` and it has a ✕,
 * Escape and a press on the scrim close it. Leave `close` out and it is a *blocking* dialog
 * — the design's confirm, ticket and commission dialogs — that closes through its own
 * buttons only ("blocking dialogs close by their buttons only", d-ov-dismiss).
 *
 * **While `busy`, nothing dismisses it.** A write in flight cannot be walked away from by
 * a stray Escape: its outcome has to land somewhere the person can see it.
 */

/** Whether anything other than the dialog's own buttons may close it right now. */
export function dismissible(close: (() => void) | undefined, busy: boolean | undefined): boolean {
  return close !== undefined && busy !== true;
}

/**
 * Where Tab goes when it would leave the dialog: from the last stop forward to the first,
 * from the first (or from nothing focusable, the title) back to the last. `undefined` is
 * "inside, let the browser move it".
 */
export function trapStep(index: number, count: number, backward: boolean): number | undefined {
  if (count === 0) return undefined;
  if (backward) return index <= 0 ? count - 1 : undefined;
  return index === -1 || index >= count - 1 ? 0 : undefined;
}

/**
 * The dialogs open right now, oldest first. Only the newest holds focus: with two open, each
 * one's "bring focus back to me" would otherwise pull against the other's, forever.
 */
export function createDialogStack() {
  const open: object[] = [];
  return {
    push: (dialog: object) => void open.push(dialog),
    remove: (dialog: object) => {
      const at = open.indexOf(dialog);
      if (at !== -1) open.splice(at, 1);
    },
    isTop: (dialog: object) => open[open.length - 1] === dialog,
  };
}

const openDialogs = createDialogStack();

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({
  title,
  children,
  actions,
  size = 'sm',
  close,
  busy,
  initialFocus,
}: {
  title: string;
  children: ReactNode;
  /** The buttons, in one row, the main action last. */
  actions: ReactNode;
  /** `sm` (400) for a short decision, `md` (560) for a form. */
  size?: 'sm' | 'md';
  /** Given: a ✕, Escape and the scrim close the dialog through it. Absent: a blocking dialog. */
  close?: (() => void) | undefined;
  /** A write is in flight: the ✕, Escape and the scrim do nothing until it settles. */
  busy?: boolean | undefined;
  /** The id of the control to focus on open; the title otherwise, so the dialog is read from the top. */
  initialFocus?: string | undefined;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const canDismiss = dismissible(close, busy);
  const self = useRef({}).current;

  // Registered before anything below moves focus, so a dialog opened over another is already
  // the top one when its first focus lands.
  useEffect(() => {
    openDialogs.push(self);
    return () => openDialogs.remove(self);
  }, [self]);

  // The opener is captured once, as it opens, before focus moves in, and given focus back as
  // it closes. `initialFocus` is read on open only: focus must not jump while somebody types.
  // biome-ignore lint/correctness/useExhaustiveDependencies: open-time only, by design
  useEffect(() => {
    const active = document.activeElement;
    const opener = active instanceof HTMLElement && active !== document.body ? active : null;
    const first = (initialFocus && document.getElementById(initialFocus)) || heading.current;
    first?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  // Focus that leaves the dialog — a press on the scrim drops it on the page — is brought
  // back, so the page behind a modal is never where the next Tab lands.
  useEffect(() => {
    const back = (event: FocusEvent) => {
      if (!openDialogs.isTop(self)) return;
      if (panel.current && !panel.current.contains(event.target as Node)) heading.current?.focus();
    };
    document.addEventListener('focusin', back);
    return () => document.removeEventListener('focusin', back);
  }, [self]);

  // Escape is heard at the document, not on the panel: focus can be on the page's body
  // rather than in the dialog — a busy button that disables itself drops it there — and
  // Escape has to work all the same. The newest `close` and `busy`, without re-listening.
  // A menu inside the dialog stops its own Escape, so closing the menu leaves this open.
  const latest = useRef({ close, busy });
  latest.current = { close, busy };
  useEffect(() => {
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || !openDialogs.isTop(self)) return;
      const { close: shut, busy: working } = latest.current;
      if (dismissible(shut, working)) shut?.();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [self]);

  // A write that settles with focus nowhere (its button disabled itself while it ran)
  // hands focus back to the dialog, so the next Tab is inside it.
  useEffect(() => {
    if (busy || !openDialogs.isTop(self) || !panel.current || panel.current.contains(document.activeElement)) return;
    heading.current?.focus();
  }, [busy, self]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab' || !panel.current || !openDialogs.isTop(self)) return;
    const stops = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    const next = trapStep(stops.indexOf(document.activeElement as HTMLElement), stops.length, event.shiftKey);
    if (next === undefined) return;
    event.preventDefault();
    stops[next]?.focus();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'grid', placeItems: 'center' }}>
      {/* The scrim is not a control — Escape and the ✕ are the keyboard's way out — so it
          is hidden from assistive technology and only listens for a press. */}
      <div
        aria-hidden="true"
        // Pressing it must not take focus out of the dialog and leave the next Tab on the page.
        onMouseDown={(event) => event.preventDefault()}
        onClick={canDismiss ? close : undefined}
        style={{ position: 'absolute', inset: 0, background: cssVar('color.overlay.scrim') }}
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy ? true : undefined}
        // Focusable, but not a tab stop: a press on its text keeps focus in the dialog.
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={{
          outline: 'none',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: cssVar('space.md'),
          boxSizing: 'border-box',
          width: cssVar(size === 'md' ? 'size.dialog.md' : 'size.dialog.sm'),
          maxWidth: `calc(100% - 2 * ${cssVar('space.lg')})`,
          maxHeight: `calc(100% - 2 * ${cssVar('space.xl')})`,
          overflowY: 'auto',
          padding: cssVar('space.xl'),
          background: cssVar('color.bg.sheet'),
          color: cssVar('color.fg.default'),
          border: `1px solid ${cssVar('color.border.default')}`,
          borderRadius: cssVar('radius.lg'),
          boxShadow: cssVar('elevation.overlay'),
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: cssVar('space.sm') }}>
          <h2 ref={heading} id={titleId} tabIndex={-1} style={{ flex: 1, minWidth: 0, margin: 0, outline: 'none' }}>
            <Text step="title">{title}</Text>
          </h2>
          {close ? <CloseButton onPress={close} disabled={busy === true} /> : null}
        </div>
        {children}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: cssVar('space.sm'),
            paddingTop: cssVar('space.sm'),
          }}
        >
          {actions}
        </div>
      </div>
    </div>
  );
}

function CloseButton({ onPress, disabled }: { onPress: () => void; disabled: boolean }) {
  const [focused, setFocused] = useState(false);
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={onPress}
      disabled={disabled}
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={{
        display: 'inline-grid',
        placeItems: 'center',
        flexShrink: 0,
        width: cssVar('size.target.min'),
        height: cssVar('size.target.min'),
        // Pulled into the corner so the title, not the button, sets the dialog's top edge.
        margin: `calc(-1 * ${cssVar('space.md')}) calc(-1 * ${cssVar('space.md')}) 0 0`,
        padding: 0,
        border: 'none',
        borderRadius: cssVar('radius.md'),
        background: 'transparent',
        color: cssVar('color.fg.muted'),
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...(focused ? focusRing : {}),
      }}
    >
      <Icon name="x" />
    </button>
  );
}
